import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { set } from "lodash";
import {
  getParentRequests,
  approveRejectParentRequest,
} from "./ParentRequestService";

export const useParentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [selectedFields, setSelectedFields] = useState({});

  const [status, setStatus] = useState("");
  const [id_number, setIdNumber] = useState("");

  /** Fetch data */
  const fetchParentRequests = useCallback(async () => {
    setIsLoader(true);
    try {
      const response = await getParentRequests(status, id_number);
      let data = response?.data?.data || [];

      data = data.map((item) => {
        set(item, "original_data", item.original_data);
        set(item, "requested_data", item.requested_data);
        return item;
      });

      setRequests(data);
    } catch {
      enqueueSnackbar("Failed to fetch parent requests.", {
        variant: "error",
      });
    } finally {
      setIsLoader(false);
    }
  }, [status, id_number]);

  /** Load once on mount */
  useEffect(() => {
    fetchParentRequests();
  }, [fetchParentRequests]);

  /** Approve / Reject */
  const handleRequest = useCallback(
    async (id, action, comment = "") => {
      const fieldsToSend = selectedFields[id] || [];

      if (fieldsToSend.length === 0) {
        enqueueSnackbar("Please select fields to update.", {
          variant: "warning",
        });
        return;
      }

      try {
        const data = {
          request_id: id,
          action,
          comment: comment || `Request ${action}.`,
          fields: fieldsToSend,
        };

        const response = await approveRejectParentRequest(data);
        if (response?.status === 200) {
          const msg =
            response?.data?.message?.success ||
            response?.data?.message ||
            "Request processed successfully.";

          enqueueSnackbar(msg, { variant: "success" });

          setSelectedFields((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });

          fetchParentRequests();
        }
      } catch {
        enqueueSnackbar(`Failed to ${action} request.`, {
          variant: "error",
        });
      }
    },
    [selectedFields, fetchParentRequests],
  );

  /** Toggle selected fields */
  const toggleField = useCallback((rowId, key) => {
    setSelectedFields((prev) => {
      const row = prev[rowId] || [];
      if (row.includes(key)) {
        return { ...prev, [rowId]: row.filter((x) => x !== key) };
      }
      return { ...prev, [rowId]: [...row, key] };
    });
  }, []);

  /** Pagination */
  const visibleItems = requests;

  return {
    // state
    requests,
    visibleItems,
    isLoader,
    selectedFields,
    status,
    id_number,

    // setters
    setStatus,
    setIdNumber,

    // handlers
    fetchParentRequests,
    toggleField,
    handleRequest,
  };
};
